
import React, { useState, useRef } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Camera } from "lucide-react";
import { toast } from "sonner";

interface PhotoUploadSectionProps {
  currentPhotoUrl?: string;
  psychologistName: string;
  onPhotoChange: (photoUrl: string) => void;
  editing: boolean;
}

const PhotoUploadSection = ({ 
  currentPhotoUrl, 
  psychologistName, 
  onPhotoChange, 
  editing 
}: PhotoUploadSectionProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(currentPhotoUrl);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione apenas arquivos de imagem.');
      return;
    }

    // Validar tamanho (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5MB.');
      return;
    }

    setUploading(true);

    try {
      // Criar preview local
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setPreviewUrl(result);
        onPhotoChange(result);
        toast.success('Foto atualizada com sucesso!');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Erro ao processar a imagem.');
    } finally {
      setUploading(false);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="relative group">
        <Avatar className="h-32 w-32">
          <AvatarImage src={previewUrl} />
          <AvatarFallback className="text-2xl">
            {psychologistName?.charAt(0)?.toUpperCase() || 'P'}
          </AvatarFallback>
        </Avatar>
        
        {editing && (
          <div className="absolute inset-0 bg-black bg-opacity-70 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={triggerFileSelect}
              disabled={uploading}
              className="text-white hover:text-white hover:bg-transparent flex flex-col items-center gap-1"
            >
              <Camera className="h-5 w-5" />
              <span className="text-xs">Alterar Foto</span>
            </Button>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {uploading && (
        <p className="text-sm text-muted-foreground">Processando imagem...</p>
      )}
    </div>
  );
};

export default PhotoUploadSection;
